/**
 * The shed film: thirty seconds from a garage with no room left in it to a shed in the
 * yard, by way of the designer, a ten dollar plan, the lumber yard and the build guide.
 *
 * Six scenes on one clock. The build follows the guide's own steps in the guide's own
 * order and under its own titles — runners, floor, deck, walls framed flat and stood up,
 * cap plates, rafters, siding, roof, doors — because the film is selling that guide, and
 * a film that built a shed some other way would be selling a different one. Nothing in
 * it is a figure from a real plan: the designer's numbers are the shopper's own choices,
 * and the price is the site's (`purchase/price.ts`).
 */

import type { Beat, Film, Frame, Site, View } from "./film.js";
import type { Ink, Pt } from "./ink.js";
import { BUY, designScene, knob, option, type Control, type DesignScript } from "./designer.js";
import { drawDog } from "./dog.js";
import { driveScene, shopScene, type Shopping } from "./errands.js";
import {
  CROUCH,
  LEAN_BACK,
  STAND,
  blend,
  drawFigure,
  hammer,
  hammerStanding,
  idle,
  impact,
  joints,
  walk,
  type Figure,
  type Pose,
} from "./rig.js";
import {
  FLOOR,
  NOTHING,
  TEN_BY_TWELVE,
  drawShed,
  frontTop,
  project,
  quickBuild,
  roofHeight,
  type Progress,
  type Projection,
  type Shape,
} from "./shed.js";
import {
  GROUND,
  bird,
  boardOnShoulder,
  caption,
  dust,
  garage,
  hammerInHand,
  impactStar,
  junk,
  ladder,
  landscape,
  mower,
  note,
  shoot,
  sparkle,
  thought,
  titleBlock,
  wipe,
  type Camera,
  type Junk,
} from "./props.js";
import { clamp, cueAt, ease, mix, pulse, span } from "./timing.js";
import { blowsOf, guideCards, shadow, yard, type Day, type Hammering, type Step } from "./yard.js";

/** When each scene starts. */
export const AT = {
  garage: 0,
  design: 4.6,
  shop: 10.4,
  drive: 13.6,
  build: 15.2,
  done: 25.8,
  card: 27.4,
  end: 30,
};

function beats(site: Site): readonly Beat[] {
  return [
    {
      at: AT.garage,
      caption: "Out of room?",
      action: "A builder heaves open the garage door, and everything stored behind it comes down on them.",
    },
    { at: 2.4, action: "Buried to the neck, the builder pictures a shed." },
    {
      at: AT.design,
      caption: "Design it to fit.",
      action:
        "In the shed designer, the builder drags the width and the length, tries a lean-to, a saltbox " +
        "and a gable roof, and picks double doors.",
    },
    {
      at: 8.65,
      caption: `Get the plans — ${site.price}`,
      action: "One click on Buy plans, and the framing drawings, the cut list and the build guide come off the printer.",
    },
    {
      at: AT.shop,
      caption: "Shop the list.",
      action: "At the lumber yard the builder fills a cart, ticking off the shopping list line by line.",
    },
    { at: AT.drive, action: "Home again, with the lumber on the roof and the dog at the window." },
    {
      at: AT.build,
      caption: "Build it, step by step.",
      action:
        "The shed goes up in the build guide's order: runners, the floor frame, the deck, the walls framed " +
        "flat and stood up, the cap plates, the rafters, the siding, the roof and the doors.",
    },
    {
      at: AT.done,
      caption: "Your shed. Your size.",
      action: "The mower rolls into the finished shed, the doors swing shut, and the builder leans on it with a thumbs up.",
    },
    {
      at: AT.card,
      action: `${site.brand}: build plans drawn to your dimensions, ${site.price}.`,
    },
  ];
}

export const shedFilm: Film = {
  duration: AT.end,
  title: "From plan to shed in thirty seconds",
  slug: "shed",
  summary:
    "A drawn film: a builder runs out of room in the garage, designs a shed to fit, buys the plans, " +
    "shops the list and builds it step by step.",
  lead: (site) =>
    `Out of room, a shed designed to fit, the plans for ${site.price}, a trip for exactly the lumber on the ` +
    "list, and the build — in the order the plan's own build guide puts it.",
  beats,
  draw,
};

function draw(frame: Frame): void {
  const { t, ink, view, site } = frame;
  if (t < AT.design) {
    garageScene(ink, view, t - AT.garage);
  } else if (t < AT.shop) {
    designScene(ink, view, t - AT.design, site, DESIGNING);
  } else if (t < AT.drive) {
    shopScene(ink, view, t - AT.shop, SHOPPING);
  } else if (t < AT.build) {
    driveScene(ink, view, t - AT.drive);
  } else {
    buildScene(ink, view, t - AT.build, site);
  }

  for (const line of captions(site)) {
    caption(ink, line.text, t, line.start, line.end, { accent: line.accent });
  }
  for (const at of WIPES) {
    wipe(ink, view, t, at);
  }
}

/** The film's own words on screen, and when: in the film's seconds. */
export function captions(site: Site): { text: string; start: number; end: number; accent?: string }[] {
  return [
    { text: "Out of room?", start: 1.45, end: 3.85 },
    { text: "Design it to fit.", start: 4.95, end: 8.5 },
    { text: `Get the plans — ${site.price}`, start: 8.68, end: 10.05, accent: site.price },
    { text: "Shop the list.", start: 10.7, end: 13.25 },
    { text: "Build it, step by step.", start: 15.5, end: 17.9 },
    { text: "Your shed. Your size.", start: 26.0, end: 27.3 },
  ];
}

/** Where a sheet of paper is pulled across the frame between scenes. */
export const WIPES: readonly number[] = [AT.design, AT.shop, AT.drive, AT.build];

// ---- 1. the garage --------------------------------------------------------------------

export interface Flight {
  kind: Junk;
  from: Pt;
  to: Pt;
  turn: number;
  rest: number;
}

export const AVALANCHE: readonly Flight[] = [
  { kind: "box", from: { x: -60, y: 200 }, to: { x: -70, y: 234 }, turn: 2, rest: 0.05 },
  { kind: "box", from: { x: 20, y: 160 }, to: { x: 30, y: 234 }, turn: -3, rest: -0.06 },
  { kind: "tire", from: { x: -40, y: 220 }, to: { x: -150, y: 238 }, turn: -4, rest: 0 },
  { kind: "bike", from: { x: -110, y: 170 }, to: { x: -226, y: 228 }, turn: 3, rest: 0.1 },
  { kind: "can", from: { x: 90, y: 170 }, to: { x: 112, y: 242 }, turn: 5, rest: 0.25 },
  { kind: "ball", from: { x: -20, y: 150 }, to: { x: -288, y: 245 }, turn: 6, rest: 0 },
  { kind: "kayak", from: { x: 0, y: 90 }, to: { x: -142, y: 168 }, turn: -1.2, rest: -0.18 },
  { kind: "rake", from: { x: 110, y: 110 }, to: { x: 150, y: 196 }, turn: 4, rest: -1.15 },
  { kind: "lamp", from: { x: -90, y: 90 }, to: { x: 72, y: 172 }, turn: -5, rest: 0.4 },
  { kind: "mower", from: { x: 60, y: 210 }, to: { x: 206, y: 238 }, turn: 0, rest: 0 },
];

const BUBBLE = { x: 168, y: -40 };

/** The garage scene's moments, in its own seconds: the picture and the score both read them. */
export const GARAGE = {
  door: [0.25, 0.8] as const,
  /** The junk comes out one piece after another, each in the air for `flight`. */
  avalanche: { start: 0.82, step: 0.05, flight: 0.42 },
  pop: 1.85,
  think: [2.4, 3.3] as const,
  /** The shed in the thought bubble, built start to finish. */
  vision: [2.85, 3.6] as const,
  happy: 3.3,
  zoom: [3.85, 4.6] as const,
};

/** Where a piece of junk lands, in the scene's seconds. */
export function junkLands(index: number): number {
  const { start, step, flight } = GARAGE.avalanche;
  return start + index * step + flight;
}

function garageScene(ink: Ink, view: View, u: number): void {
  const zoom = ease.in(span(u, ...GARAGE.zoom));
  const camera: Camera = { x: mix(-40, BUBBLE.x, zoom), y: mix(110, BUBBLE.y, zoom), zoom: mix(1.32, 5.5, zoom) };
  shoot(
    ink,
    camera,
    (v) => {
      landscape(ink, v, GROUND);
      const open = ease.inOut(span(u, ...GARAGE.door));
      garage(ink, open);

      // The dog, startled into the air and landing to watch.
      const jump = span(u, 0.85, 1.3);
      drawDog(ink, {
        x: -336 - jump * 14,
        ground: GROUND,
        facing: 1,
        mode: jump > 0 && jump < 1 ? "jump" : "sit",
        air: jump,
        t: u,
      });

      // The builder: crouch, heave, recoil — and then under it all.
      const handle = { x: 0, y: mix(GROUND - 18, 4, open) };
      if (u < 1.08) {
        let pose: Pose;
        let reach: Pt | undefined;
        let x = -46;
        if (u < 0.25) {
          pose = CROUCH;
          reach = handle;
        } else if (u < 0.8) {
          pose = blend(CROUCH, { ...STAND, armA: 2.6, armB: 2.4, elbowA: 0.2, elbowB: 0.2 }, ease.out(span(u, 0.25, 0.7)));
          reach = handle.y > GROUND - 190 ? handle : undefined;
        } else {
          const k = span(u, 0.8, 1.08);
          pose = { ...STAND, lean: -0.5 * k, armA: 2.7, armB: 2.2, elbowA: 0.6, elbowB: 0.6, legA: 0.4 * k, legB: -0.2 };
          x = -46 - k * 20;
        }
        drawFigure(ink, pose, {
          x,
          ground: GROUND,
          facing: 1,
          reachA: reach,
          reachB: reach,
          eyes: u > 0.8 ? "wide" : "open",
          mouth: u > 0.8 ? "o" : "flat",
        });
      }

      AVALANCHE.forEach((flight, i) => {
        const start = junkLands(i) - GARAGE.avalanche.flight;
        const k = span(u, start, junkLands(i));
        if (k <= 0) {
          return;
        }
        const arc = Math.sin(k * Math.PI) * (flight.kind === "ball" ? 160 : 120);
        const x = mix(flight.from.x, flight.to.x, k);
        const y = mix(flight.from.y, flight.to.y, ease.in(k)) - arc;
        const rotation = mix(flight.turn, 0, ease.out(k)) + flight.rest;
        junk(ink, flight.kind, x, y, rotation, k < 1 ? u : 0);
      });

      // Out of the top of the pile, cap askew.
      if (u >= GARAGE.pop) {
        const pop = ease.back(span(u, GARAGE.pop, GARAGE.pop + 0.3));
        const blink = u > 2.6 && u < 2.7;
        const happy = u > GARAGE.happy;
        drawFigure(ink, STAND, {
          x: -18,
          y: mix(214, 152, pop) + 72,
          facing: 1,
          headOnly: true,
          look: 0.2,
          capTilt: 0.45,
          eyes: blink ? "closed" : happy ? "happy" : "open",
          mouth: happy ? "grin" : "flat",
        });
        const think = span(u, ...GARAGE.think);
        thought(ink, { x: 4, y: 112 }, BUBBLE, think, 64);
        const build = span(u, ...GARAGE.vision);
        if (build > 0) {
          drawShed(ink, { x: BUBBLE.x - 50, y: BUBBLE.y + 52, scale: 6.4 }, TEN_BY_TWELVE, quickBuild(build));
        }
      }
    },
    view,
  );
}

// ---- 2. designing it, and buying the plans ------------------------------------------------

/** The design scene's moments, in its own seconds. */
export const DESIGN = {
  widthDrag: [0.62, 1.15] as const,
  lengthDrag: [1.55, 2.1] as const,
  roofClicks: [2.5, 2.8, 3.1] as const,
  doorClick: 3.5,
  buyClick: 4.05,
  delighted: 4.3,
  pullBack: [4.4, 5.0] as const,
  printing: [4.6, 5.5] as const,
  pages: { out: 4.65, step: 0.2, fly: [0.2, 0.55] as const },
  stand: [4.85, 5.2] as const,
  hop: [5.3, 5.6] as const,
};

/** What the designer on screen shows at `u`: the sliders' values and the choices made. */
export function designState(u: number): {
  width: number;
  length: number;
  ridge: number;
  roof: 0 | 1 | 2;
  doubleDoor: boolean;
  press: number;
  paid: number;
} {
  const [lean, salt, gable] = DESIGN.roofClicks;
  return {
    width: mix(8, 10, ease.inOut(span(u, ...DESIGN.widthDrag))),
    length: mix(8, 12, ease.inOut(span(u, ...DESIGN.lengthDrag))),
    ridge:
      u < salt
        ? mix(0.5, 1, ease.inOut(span(u, lean, lean + 0.2)))
        : u < gable
          ? mix(1, 0.72, ease.inOut(span(u, salt, salt + 0.2)))
          : mix(0.72, 0.5, ease.inOut(span(u, gable, gable + 0.2))),
    roof: u >= gable ? 0 : u >= salt ? 1 : u >= lean ? 2 : 0,
    doubleDoor: u >= DESIGN.doorClick,
    press: pulse(u, DESIGN.buyClick, DESIGN.buyClick + 0.2),
    paid: span(u, DESIGN.buyClick + 0.2, DESIGN.buyClick + 0.3),
  };
}

/** The panel as it reads with the choices in `state`: size, then roof, then doors. */
function controls(state: ReturnType<typeof designState>): Control[] {
  return [
    { kind: "slider", label: "Width", value: state.width, min: 6, max: 14 },
    { kind: "slider", label: "Length", value: state.length, min: 6, max: 16 },
    { kind: "choice", label: "Roof", options: ["Gable", "Saltbox", "Lean-to"], chosen: state.roof },
    { kind: "choice", label: "Doors", options: ["Single", "Double"], chosen: state.doubleDoor ? 1 : 0 },
  ];
}

const PANEL = controls(designState(0));
const [LEAN_TO, SALTBOX, GABLE] = DESIGN.roofClicks;

/** Sized up from eight by eight, every roof tried, double doors, and bought. */
export const DESIGNING: DesignScript = {
  at(u) {
    const state = designState(u);
    const { width, length, ridge, doubleDoor } = state;
    return {
      controls: controls(state),
      width,
      length,
      shape: { ...TEN_BY_TWELVE, width, depth: length, ridge, doubleDoor },
      press: state.press,
      paid: state.paid,
    };
  },
  keys: [
    { at: 0, x: 150, y: 175 },
    { at: 0.6, ...knob(PANEL, 0, 8) },
    { at: DESIGN.widthDrag[0], ...knob(PANEL, 0, 8) },
    { at: DESIGN.widthDrag[1], ...knob(PANEL, 0, 10) },
    { at: 1.5, ...knob(PANEL, 1, 8) },
    { at: DESIGN.lengthDrag[0], ...knob(PANEL, 1, 8) },
    { at: DESIGN.lengthDrag[1], ...knob(PANEL, 1, 12) },
    { at: LEAN_TO - 0.05, ...option(PANEL, 2, 2) },
    { at: SALTBOX - 0.05, ...option(PANEL, 2, 1) },
    { at: GABLE - 0.05, ...option(PANEL, 2, 0) },
    { at: DESIGN.doorClick - 0.05, ...option(PANEL, 3, 1) },
    { at: DESIGN.buyClick - 0.08, x: BUY.x + 6, y: BUY.y + 1 },
    { at: 5.8, x: BUY.x + 6, y: BUY.y + 1 },
  ],
  clicks: [DESIGN.widthDrag[0], DESIGN.lengthDrag[0], LEAN_TO, SALTBOX, GABLE, DESIGN.doorClick, DESIGN.buyClick],
  timing: DESIGN,
};

// ---- 3. the lumber yard, and 4. the drive home ----------------------------------------------

/** The bill, a line at a time onto the cart, in the shop scene's seconds. */
export const SHOPPING: Shopping = {
  list: [
    { kind: "studs", height: 21, line: "Studs", at: 0.45 },
    { kind: "joists", height: 30, line: "Joists", at: 0.85 },
    { kind: "plywood", height: 14, line: "Plywood", at: 1.25 },
    { kind: "siding", height: 12, line: "Siding", at: 1.65 },
    { kind: "shingles", height: 18, line: "Shingles", at: 2.05 },
  ],
  aisle: [0, 3.05],
};

// ---- 5. building it, and 6. done ------------------------------------------------------------

const SHED: Projection = { x: -170, y: GROUND, scale: 26 };

/** The build's day: the sun across in eleven seconds, gold from six. */
const DAY: Day = { length: 11, evening: [6, 12], tree: -430 };

/** The build, on the scene's own clock: each stage's start and end, in the guide's order. */
export const PLAN = {
  runners: [0.25, 0.95],
  joists: [0.95, 2.0],
  deck: [2.0, 2.6],
  left: [2.6, 2.95, 3.25],
  back: [3.25, 3.55, 3.85],
  right: [3.85, 4.2, 4.5],
  front: [4.5, 4.95, 5.7],
  caps: [5.7, 6.1],
  rafters: [6.1, 7.5],
  siding: [7.5, 8.7],
  trim: [8.55, 9.0],
  roofDeck: [8.7, 9.25],
  shingles: [9.2, 9.9],
  doors: [9.9, 10.5],
} as const;

/**
 * The guide's steps, under the titles the plan prints them with, as the card shows them.
 * `test/film.test.ts` finds every one of them in the engine's guide (`guide.cpp`).
 */
export const STEPS: readonly Step[] = [
  { at: 0.25, title: "Set the runners" },
  { at: 0.95, title: "Frame the floor" },
  { at: 2.0, title: "Lay the deck" },
  { at: 2.6, title: "Frame the walls flat" },
  { at: 4.95, title: "Stand the walls and brace them" },
  { at: 5.7, title: "Lap the cap plates over the corners" },
  { at: 6.1, title: "Cut and hang the rafters" },
  { at: 7.5, title: "Sheathe the walls" },
  { at: 8.7, title: "Deck the roof and cover it" },
  { at: 9.9, title: "Hang the doors" },
];

/** How far the build is at `u` seconds into the scene. */
export function buildProgress(u: number, swing = 0): Progress {
  const s = (stage: readonly [number, number]): number => span(u, stage[0], stage[1]);
  const wall = (w: readonly [number, number, number]): { frame: number; raise: number } => ({
    frame: span(u, w[0], w[1]),
    raise: span(u, w[1], w[2]),
  });
  return {
    ...NOTHING,
    runners: s(PLAN.runners),
    joists: s(PLAN.joists),
    deck: s(PLAN.deck),
    left: wall(PLAN.left),
    back: wall(PLAN.back),
    right: wall(PLAN.right),
    front: wall(PLAN.front),
    caps: s(PLAN.caps),
    rafters: s(PLAN.rafters),
    siding: s(PLAN.siding),
    trim: s(PLAN.trim),
    roofDeck: s(PLAN.roofDeck),
    shingles: s(PLAN.shingles),
    doors: s(PLAN.doors),
    swing,
  };
}

/** Where the builder works through the build: a spot per stretch of it, popped between. */
export const SPOTS = [0, 0.95, 2.6, 4.5, 5.7, 7.5, 8.7, 9.9, 10.6];

/** How fast the builder hammers at each spot (`yard.ts`, `Hammering`). */
export const HAMMERING: Hammering = {
  1: { rate: 2.6, offset: 0, pan: -0.45 },
  2: { rate: 2.6, offset: 0.3, pan: -0.5 },
  4: { rate: 2.4, offset: 0, pan: 0.2 },
  5: { rate: 2.6, offset: 0, pan: -0.45 },
  6: { rate: 2.8, offset: 0, pan: 0.15 },
  7: { rate: 2.6, offset: 0, pan: 0.15 },
};

function blow(spot: number, u: number): number {
  const hammering = HAMMERING[spot]!;
  return u * hammering.rate + hammering.offset;
}

/** Every blow of the hammer in the build, in the build scene's seconds, with where it is. */
export function hammerBlows(): { at: number; pan: number }[] {
  return blowsOf(SPOTS, HAMMERING);
}

/** The last scene's moments, in its own seconds (from `AT.done`). */
export const DONE = {
  approach: [0, 0.75] as const,
  inward: [0.75, 1.1] as const,
  open: [0.15, 0.55] as const,
  close: [1.15, 1.45] as const,
  settle: [1.1, 1.45] as const,
  dog: [1.2, 1.7] as const,
  bird: [1.0, 1.6] as const,
  song: [1.7, 2.4] as const,
  glints: [
    [1.45, 1.95],
    [1.6, 2.1],
  ] as const,
  camera: [1.6, 2.2] as const,
  card: [1.75, 2.35] as const,
};

function buildScene(ink: Ink, view: View, u: number, site: Site): void {
  const done = u - (AT.done - AT.build);
  const card = span(done, ...DONE.camera);
  const camera: Camera = { x: mix(0, 20, ease.inOut(card)), y: mix(20, 168, ease.inOut(card)), zoom: mix(1, 0.94, ease.inOut(card)) };
  const swing = done < 0 ? 0 : span(done, ...DONE.open) - span(done, ...DONE.close);
  const progress = buildProgress(u, clamp(swing));
  const shape = TEN_BY_TWELVE;
  shoot(
    ink,
    camera,
    (v) => {
      const arc = yard(ink, v, u, DAY);

      // The shed's shadow, swinging round as the sun crosses.
      const height =
        FLOOR * progress.deck +
        7 * ((progress.left.raise + progress.back.raise + progress.right.raise + progress.front.raise) / 4) +
        3.3 * progress.rafters;
      shadow(ink, SHED, shape, height, arc);

      // What is left of the lumber.
      const left = Math.round(9 * (1 - clamp(u / 10)));
      for (let i = 0; i < left; i += 1) {
        ink.rect(-372 + (i % 2) * 6, GROUND - 10 - i * 9, 132, 9, { fill: ink.palette.wood, width: 1.6, offset: 0.6, overshoot: false });
      }

      dogInTheYard(ink, u, done);

      const spot = cueAt(u, SPOTS);
      const popped = span(u, SPOTS[spot] ?? 0, (SPOTS[spot] ?? 0) + 0.3);
      const inside = (): void => {
        if (spot === 3) {
          lift(ink, u, shape, popped);
        }
      };
      drawShed(ink, SHED, shape, progress, { inside });
      if (spot !== 3 && done < 0) {
        builderAtWork(ink, u, spot, popped);
      }
      if (done >= 0) {
        finale(ink, done, shape);
      }
    },
    view,
  );

  guideCards(ink, u, STEPS, span(done, 0, 0.2));
  titleBlock(ink, span(done, ...DONE.card), site, {
    kind: "SHED PLANS",
    line1: "Build plans drawn",
    line2: "to your dimensions.",
  });
}

function builderAtWork(ink: Ink, u: number, spot: number, popped: number): void {
  const puff = (x: number, y: number): void => dust(ink, x, y, popped < 1 ? popped : 0, 1.2);
  if (spot <= 0) {
    // Carrying a board over from the pile.
    const k = ease.inOut(span(u, 0, 0.85));
    const x = mix(-300, -212, k);
    const pose: Pose = { ...walk(x / 40), armA: 2.75, elbowA: 0.6 };
    const figure: Figure = { x, ground: GROUND, facing: 1, eyes: "open", mouth: "smile" };
    const j = joints(pose, figure);
    drawFigure(ink, pose, { ...figure, reachA: { x: j.shoulder.x + 6, y: j.shoulder.y - 16 }, hold: boardOnShoulder(130) });
    return;
  }
  const working: Record<number, () => void> = {
    1: () => {
      const phase = blow(1, u);
      drawFigure(ink, hammer(phase), { x: -230, ground: GROUND, facing: 1, hold: hammerInHand, eyes: "open", mouth: "flat" });
      impactStar(ink, -172, 232, impact(phase));
      puff(-230, GROUND);
    },
    2: () => {
      const phase = blow(2, u);
      drawFigure(ink, hammer(phase), { x: -260, ground: GROUND, facing: 1, hold: hammerInHand, eyes: "open", mouth: "smile" });
      impactStar(ink, -200, 240, impact(phase));
      puff(-260, GROUND);
    },
    4: () => {
      const foot = { x: 128, y: GROUND + 6 };
      const top = { x: 88, y: 46 };
      ladder(ink, foot, top);
      const phase = blow(4, u);
      drawFigure(ink, hammerStanding(phase), { x: 108, ground: 158, facing: -1, hold: hammerInHand, eyes: "open", mouth: "flat" });
      impactStar(ink, 52, 30, impact(phase));
      puff(108, 158);
    },
    5: () => {
      const phase = blow(5, u);
      drawFigure(ink, hammerStanding(phase), { x: -225, ground: GROUND, facing: 1, hold: hammerInHand, eyes: "open", mouth: "smile" });
      impactStar(ink, -160, 150, impact(phase));
      puff(-225, GROUND);
    },
    6: () => {
      const phase = blow(6, u);
      const spotOnRoof = project(SHED, 7.6, roofHeight(TEN_BY_TWELVE, 7.6) + 0.3, 3 + clamp((u - 8.7) / 1.2) * 5);
      drawFigure(ink, hammer(phase), { x: spotOnRoof.x, ground: spotOnRoof.y, facing: 1, hold: hammerInHand, eyes: "open", mouth: "flat" });
      impactStar(ink, spotOnRoof.x + 58, spotOnRoof.y - 26, impact(phase), 0.8);
      puff(spotOnRoof.x, spotOnRoof.y);
    },
    7: () => {
      const phase = blow(7, u);
      drawFigure(ink, hammerStanding(phase), { x: 92, ground: GROUND, facing: -1, hold: hammerInHand, eyes: "open", mouth: "grin" });
      impactStar(ink, 40, 160, impact(phase));
      puff(92, GROUND);
    },
  };
  working[spot]?.();
}

/** Standing the front wall: up from a crouch at its top plate, walking it upright. */
function lift(ink: Ink, u: number, shape: Shape, popped: number): void {
  const raise = span(u, PLAN.front[1], PLAN.front[2]);
  const angle = (Math.PI / 2) * ease.out(raise);
  const z = shape.wall * Math.cos(angle) + 0.9;
  const feet = project(SHED, shape.width * 0.42, FLOOR, z);
  const hands = frontTop(SHED, shape, raise);
  const pose = blend(CROUCH, { ...STAND, lean: 0.08, armA: 2.4, armB: 2.3, elbowA: 0.3, elbowB: 0.3 }, ease.inOut(span(raise, 0, 0.7)));
  drawFigure(ink, pose, {
    x: feet.x + 8,
    ground: feet.y,
    facing: -1,
    reachA: hands,
    reachB: { x: hands.x - 14, y: hands.y + 2 },
    eyes: raise > 0.2 && raise < 0.9 ? "closed" : "open",
    mouth: raise > 0.2 && raise < 0.9 ? "grin" : "o",
  });
  dust(ink, feet.x, feet.y, popped < 1 ? popped : 0, 1);
  if (raise >= 1) {
    sparkle(ink, hands.x, hands.y - 20, span(u, PLAN.front[2], PLAN.front[2] + 0.3));
  }
}

function dogInTheYard(ink: Ink, u: number, done: number): void {
  if (done >= DONE.dog[0]) {
    // Awake for the finish, sitting by the builder.
    const come = ease.inOut(span(done, ...DONE.dog));
    drawDog(ink, {
      x: mix(-412, -300, come),
      ground: GROUND + 4,
      facing: 1,
      mode: come > 0 && come < 1 ? "run" : "sit",
      phase: done * 6,
      t: u,
    });
    return;
  }
  if (u < 1.1) {
    const k = ease.out(span(u, 0, 1.1));
    drawDog(ink, { x: mix(-640, -412, k), ground: GROUND + 4, facing: 1, mode: "run", phase: u * 5, carry: true, t: u });
    return;
  }
  ink.rect(-380, GROUND - 4, 46, 6, { fill: ink.palette.wood, width: 2, offset: 1 });
  drawDog(ink, { x: -412, ground: GROUND + 4, facing: 1, mode: u > 7 ? "sleep" : "sit", t: u });
}

function finale(ink: Ink, done: number, shape: Shape): void {
  const p = ink.palette;
  const door = project(SHED, shape.width / 2, FLOOR, 0);
  // The mower, wheeled up to the doors and rolled in.
  const approach = ease.inOut(span(done, ...DONE.approach));
  const inward = ease.inOut(span(done, ...DONE.inward));
  if (inward < 1) {
    const ground = project(SHED, shape.width / 2, mix(0, FLOOR, inward), mix(-1.6, 3, inward));
    const mx = inward > 0 ? ground.x : mix(-360, door.x, approach);
    const my = (inward > 0 ? ground.y : GROUND) - 24;
    const { ctx } = ink;
    ctx.save();
    if (inward > 0.35) {
      const jamb = (x: number, y: number): Pt => project(SHED, x, y, 0);
      const width = shape.doubleDoor ? 5 : 3;
      const u0 = (shape.width - width) / 2;
      const corners = [jamb(u0, FLOOR), jamb(u0 + width, FLOOR), jamb(u0 + width, FLOOR + 6.2), jamb(u0, FLOOR + 6.2)];
      ctx.beginPath();
      corners.forEach((c, i) => (i === 0 ? ctx.moveTo(c.x, c.y) : ctx.lineTo(c.x, c.y)));
      ctx.closePath();
      ctx.clip();
    }
    const grip = mower(ink, mx, my, done * 14);
    ctx.restore();
    if (inward <= 0.1) {
      const pose: Pose = { ...walk(mx / 50), lean: 0.25 };
      drawFigure(ink, pose, { x: grip.x - 46, ground: GROUND, facing: 1, reachA: grip, reachB: { x: grip.x - 3, y: grip.y + 6 }, mouth: "grin" });
    } else {
      drawFigure(ink, idle(done), { x: door.x - 120, ground: GROUND, facing: 1, eyes: "happy", mouth: "grin" });
    }
    return;
  }
  // Done: leaning on the corner, thumb up.
  const settle = ease.out(span(done, ...DONE.settle));
  const x = mix(door.x - 120, -204, settle);
  drawFigure(ink, blend(idle(done), LEAN_BACK, settle), {
    x,
    ground: GROUND,
    facing: -1,
    look: 0.25,
    eyes: "happy",
    mouth: "grin",
    thumbA: settle > 0.6,
  });
  // A bird on the ridge, and a glint off the window.
  const ridge = project(SHED, shape.width / 2, roofHeight(shape, shape.width / 2) + 0.35, -0.5);
  const fly = ease.out(span(done, ...DONE.bird));
  bird(ink, mix(ridge.x + 380, ridge.x, fly), mix(ridge.y - 260, ridge.y - 8, fly), done * 30, fly >= 1);
  note(ink, ridge.x + 18, ridge.y - 28, span(done, ...DONE.song));
  const window = project(SHED, shape.width, FLOOR + 5.2, shape.depth / 2 - 0.6);
  sparkle(ink, window.x, window.y, span(done, ...DONE.glints[0]), 16);
  sparkle(ink, door.x + 40, door.y - 120, span(done, ...DONE.glints[1]), 12);
  void p;
}
